import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import "./TripHistoryModal.css";
import { getTripHistoryLimit } from "../utils/featureAccess";

const TripHistoryModal = ({ onClose, currentUser, restrictTripHistory }) => {
  const [vehicles, setVehicles] = useState([]);
  const [plateNumber, setPlateNumber] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const userTier = currentUser?.tier || "basic";
  const tripLimitDays = getTripHistoryLimit(userTier);

  useEffect(() => {
    const fetchVehiclesFromFirestore = async () => {
      try {
        let querySnapshot;
        if (userTier === "admin") {
          // Admins fetch all vehicles
          querySnapshot = await getDocs(collection(db, "vehicles"));
        } else {
          // Non-Admins fetch only their vehicles
          const q = query(
            collection(db, "vehicles"),
            where("ownerUid", "==", currentUser.uid)
          );
          querySnapshot = await getDocs(q);
        }
        const fetchedVehicles = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVehicles(fetchedVehicles);
        // Set default plate number to the first vehicle (if available)
        if (fetchedVehicles.length > 0) {
          setPlateNumber(fetchedVehicles[0].plateNumber || "");
        }
      } catch (error) {
        console.error("❌ Error fetching vehicles from Firestore:", error);
        setError("Failed to load vehicles.");
      }
    };
    fetchVehiclesFromFirestore();
  }, [currentUser, userTier]);

  const handleViewTrip = () => {
    if (!plateNumber) {
      setError("Please select a vehicle.");
      return;
    }
    if (!fromDate || !toDate) {
      setError("Please select a date range.");
      return;
    }
    const fromTimestamp = Math.floor(new Date(fromDate).getTime() / 1000);
    const toTimestamp = Math.floor(new Date(toDate).getTime() / 1000);
    if (fromTimestamp >= toTimestamp) {
      setError("From date must be before to date.");
      return;
    }
    if (toTimestamp > Math.floor(Date.now() / 1000)) {
      setError("To date cannot be in the future.");
      return;
    }
    const maxSeconds = tripLimitDays * 24 * 60 * 60;
    if (toTimestamp - fromTimestamp > maxSeconds && tripLimitDays !== Infinity) {
      setError(`Date range cannot exceed ${tripLimitDays} days for your tier.`);
      return;
    }
    navigate(
      `/trip-viewer?plate=${encodeURIComponent(plateNumber)}&from=${fromTimestamp}&to=${toTimestamp}`
    );
    onClose();
  };

  if (tripLimitDays === 0) {
    return (
      <div className="trip-modal-overlay">
        <div className="trip-modal">
          <p>You don't have access to trip history. <a href="/pricing">Upgrade</a></p>
          <button onClick={onClose} className="close-btn">❌ Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="trip-modal-overlay">
      <div className="trip-modal">
        <h2>📍 Select Trip History</h2>
        {error && <p style={{ color: "red" }}>{error}</p>}
        <label>🔢 Vehicle:</label>
        <select
          value={plateNumber}
          onChange={(e) => setPlateNumber(e.target.value)}
          disabled={loading || vehicles.length === 0}
        >
          {vehicles.length === 0 ? (
            <option value="">No vehicles available</option>
          ) : (
            vehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.plateNumber}>
                {vehicle.plateNumber || "Unknown Vehicle"}
              </option>
            ))
          )}
        </select>
        <label>📅 From Date:</label>
        <input
          type="datetime-local"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <label>📅 To Date:</label>
        <input
          type="datetime-local"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        <div className="trip-modal-actions">
          <button onClick={onClose} className="close-btn">❌ Close</button>
          <button
            onClick={handleViewTrip}
            className="view-btn"
            disabled={loading || vehicles.length === 0}
          >
            {loading ? "Loading..." : "🔍 View Trip"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripHistoryModal;